// File: src/components/UserPanel.js
import React, { useState, useEffect } from 'react';
import axios from '../api/axiosInstance';
import './UserPanel.css';
import showModal from './ui/modals/showModal';


const plateRegex = /^[A-Z0-9]{4,8}$/;

const isValidPlate = (str) => {
  const cleaned = str.toUpperCase().replace(/\s/g, '');
  return plateRegex.test(cleaned) && /[A-Z]/.test(cleaned) && /\d/.test(cleaned);
};

function UserPanel() {
  // Ustawienia zakładek: 'userData', 'reservations', 'password'
  const [activeTab, setActiveTab] = useState('userData');

  // Dane użytkownika – do wyświetlenia w zakładce "Dane Użytkownika"
  const [user, setUser] = useState({
    username: '',
    email: '',
    regCarNum: ''
  });

  // Dane zmiany hasła
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  // Rezerwacje użytkownika
  const [reservations, setReservations] = useState([]);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [errorReservations, setErrorReservations] = useState('');

  // Zakładamy, że identyfikator użytkownika jest przechowywany (na potrzeby przykładu stała)
  const [userId, setUserId] = useState(null);
  const [isMicrosoftUser, setIsMicrosoftUser] = useState(false);

  // Adres backendu
  const HOST = process.env.REACT_APP_HOST;
  const PORT = process.env.REACT_APP_API_PORT;
  const baseUrl = `https://${HOST}`;

  // Fetch danych użytkownika – zakładamy endpoint /api/auth/me (dostosuj w razie potrzeby)
  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
  
    try {
      const res = await axios.get(`${baseUrl}/api/auth/me`);
  
      setUser({
        ...res.data,
        regCarNum: Array.isArray(res.data.regCarNum)
          ? res.data.regCarNum
          : (res.data.regCarNum ? res.data.regCarNum.split(',').map(s => s.trim()) : []),
      });
      const isMicrosoftUser = user.loginMethod === 'microsoft';
      setIsMicrosoftUser(res.data.loginMethod === 'microsoft');
      setUserId(res.data.id);
      
    } catch (err) {
      console.error(err);
    }
  };
  
  useEffect(() => {
    fetchUser();
  }, [baseUrl]);
  

  // Pobieramy rezerwacje użytkownika (tylko przyszłe) przy aktywacji zakładki "Moje rezerwacje"
  useEffect(() => {
    if (activeTab === 'reservations') {
      fetchReservations();
    }
  }, [activeTab]);

  const fetchReservations = async () => {
    setLoadingReservations(true);
    setErrorReservations('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${baseUrl}/api/user/reservations`);
      // Sortujemy rezerwacje rosnąco według daty
      const sorted = res.data.sort((a, b) => new Date(a.date) - new Date(b.date));
      setReservations(sorted);
    } catch (err) {
      console.error(err);
      setErrorReservations('Błąd pobierania rezerwacji.');
    } finally {
      setLoadingReservations(false);
    }
  };

  // Regex dla walidacji hasła: minimum 14 znaków, co najmniej jedna mała, jedna duża, jeden znak specjalny
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{14,}$/;

  const evaluatePasswordStrength = (password) => {
  const lengthOK = password.length >= 14;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);

  if (!password) return '';
  if (!lengthOK) return '🔴 Za krótkie (min. 14 znaków)';
  if (!hasUpper || !hasLower) return '🟡 Dodaj duże i małe litery';
  if (!hasSpecial) return '🟡 Dodaj znak specjalny';

  return '🟢 Silne hasło';
};

  const handleChangePassword = async (e) => {
    if (!passwordsMatch) {
      setPasswordError('Hasła nie są zgodne.');
      return;
    }

    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Nowe hasło i potwierdzenie nie są zgodne.');
      return;
    }

    if (!passwordRegex.test(newPassword)) {
      setPasswordError('Hasło musi mieć minimum 14 znaków, zawierać duże i małe litery oraz przynajmniej jeden znak specjalny.');
      return;
    }

    try {
      await axios.put(`${baseUrl}/api/user/change-password`, {
        userId,
        currentPassword,
        newPassword
      });
      setPasswordSuccess('Hasło zostało zmienione pomyślnie.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err.response?.data?.message) {
      setPasswordError(err.response.data.message);
    } else {
      setPasswordError('Błąd zmiany hasła. Spróbuj ponownie później.');
    }
    }
  };

  // Funkcja obsługująca anulowanie rezerwacji – nie pozwala na usunięcie rezerwacji z przeszłości
  const handleCancelReservation = async (reservationId, reservationDate) => {
    const today = new Date().toISOString().split('T')[0];
    if (reservationDate < today) {
      showModal('Nie można anulować rezerwacji wstecz.', {type: "error"});
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${baseUrl}/api/reservations/${reservationId}`);
      showModal('Rezerwacja została anulowana.', {type: "success"});
      fetchReservations();
    } catch (err) {
      console.error(err);
      showModal('Błąd anulowania rezerwacji.', {type: "error"});
    }
  };

  return (
    <div className="user-panel-container">
      <h2>Panel Użytkownika</h2>
      <div className="tabs">
        <button
          className={activeTab === 'userData' ? 'active' : ''}
          onClick={() => setActiveTab('userData')}
        >
          Dane Użytkownika
        </button>
        <button
          className={activeTab === 'reservations' ? 'active' : ''}
          onClick={() => setActiveTab('reservations')}
        >
          Moje rezerwacje
        </button>
        {!isMicrosoftUser && (
            <button
              className={activeTab === 'password' ? 'active' : ''}
              onClick={() => setActiveTab('password')}
            >
              Hasło
            </button>
          )}
      </div>

      <div className="tab-content">
        {activeTab === 'userData' && (
          <div className="user-data">
            <h3>Dane Użytkownika</h3>
            <p><strong>Nazwa:</strong> {user.username}</p>
            <p><strong>Email:</strong> {user.email}</p>
            <div className="form-group">
              <label><strong>Numery rejestracyjne:</strong></label>
              {Array.isArray(user.regCarNum) && user.regCarNum.map((num, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    value={num}
                    onChange={e => {
                      const raw = e.target.value;
                      const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
                    
                      const updated = [...user.regCarNum];
                      updated[index] = cleaned;
                      setUser({ ...user, regCarNum: updated });
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const updated = user.regCarNum.filter((_, i) => i !== index);
                      setUser({ ...user, regCarNum: updated });
                    }}
                    style={{ backgroundColor: '#ddd', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px' }}
                  >
                    🗑️
                  </button>
                </div>
              ))}
              {user.regCarNum.length < 3 && (
                <button
                  type="button"
                  onClick={() => setUser({ ...user, regCarNum: [...user.regCarNum, ""] })}
                  style={{ backgroundColor: '#007bff', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', marginTop: '0.5rem' }}
                >
                  + Dodaj puste miejsce
                </button>
              )}
            </div>
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const payload = {
                    regCarNum: user.regCarNum.filter(Boolean),
                  };

                  const invalids = payload.regCarNum.filter(num => !isValidPlate(num));
                  if (invalids.length > 0) {
                    showModal(`Nieprawidłowe numery: ${invalids.join(', ')}. Numer musi mieć 4-8 znaków, zawierać litery i cyfry.`, {type: "error"});
                    return;
                  }
                  
                  await axios.put(`${baseUrl}/api/user/update-regnums`, payload);

                  showModal('Numery zostały zapisane.', {type: "success"});
                  fetchUser(); // 🔄 odśwież dane użytkownika
                } catch (err) {
                  console.error(err);
                  showModal('Błąd zapisu numerów rejestracyjnych.', {type: "error"});
                }
              }}
              className="change-password-button"
            >
              💾 Zapisz zmiany
            </button>

          </div>
        )}

        {activeTab === 'reservations' && (
          <div className="user-reservations">
            <h3>Moje rezerwacje</h3>
            {loadingReservations ? (
              <p>Ładowanie rezerwacji...</p>
            ) : errorReservations ? (
              <p className="error">{errorReservations}</p>
            ) : reservations.length === 0 ? (
              <p>Brak rezerwacji.</p>
            ) : (
              <table className="reservations-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Miejsce</th>
                    <th>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((resv) => {
                    const resDate = new Date(resv.date).toISOString().split('T')[0];
                    const today = new Date().toISOString().split('T')[0];
                    const canCancel = resDate >= today;
                    const spotNumber = resv.ParkingSpot ? resv.ParkingSpot.spotNumber : resv.parkingSpotId;
                    
                    // Formatowanie daty
                    const date = new Date(resv.date);
                    const formattedDate = date.toLocaleDateString('pl-PL', {
                      weekday: 'long',
                      day: '2-digit',
                      month: '2-digit',
                    });

                    const parts = formattedDate.split(', ');
                    
                    // Przestawienie kolejności elementów i dodanie myślnika
                    const reorderedDate = `${parts[1]} - ${parts[0]}`;

                    return (
                      <tr key={resv.id}>
                        <td>{reorderedDate}</td>
                        <td>{spotNumber}</td>
                        <td>
                          <button
                            onClick={() => handleCancelReservation(resv.id, resDate)}
                            disabled={!canCancel}
                          >
                            Usuń
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'password' && !isMicrosoftUser && (
          <div className="password-data">
            <h3>Zmień hasło</h3>
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label>Obecne hasło:</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Nowe hasło:</label>
                <small style={{ fontSize: '0.85rem', color: '#666' }}>
                  Hasło musi mieć min. 14 znaków, zawierać duże i małe litery oraz znak specjalny.
                </small>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewPassword(val);
                    setPasswordStrength(evaluatePasswordStrength(val));
                    setPasswordsMatch(val === confirmPassword);
                  }}
                  required
                />
                {passwordStrength && (
                  <p className="password-feedback">{passwordStrength}</p>
                )}
              </div>
              <div className="form-group">
                <label>Potwierdź nowe hasło:</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    const val = e.target.value;
                    setConfirmPassword(val);
                    setPasswordsMatch(val === newPassword);
                  }}
                  required
                />
                {!passwordsMatch && (
                  <p className="error">Hasła nie są zgodne.</p>
                )}
              </div>
              {passwordError && <p className="error">{passwordError}</p>}
              {passwordSuccess && <p className="success">{passwordSuccess}</p>}
              <button type="submit" className="change-password-button">
                Zmień hasło
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserPanel;
